import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHero, StatusPill, StatHero } from "@/components/shared/Visual";
import { ShieldCheck, MapPin, CalendarDays, Users2, Lock, ChevronRight, Activity } from "lucide-react";

const KIND = {
  awaiting_ops_approval: "standby", accepted: "active", team_being_built: "tracked",
  staff_awaiting_confirmation: "tracked", deployment_confirmed: "tracked",
  staff_en_route: "live", live: "live", completed: "match", closed: "neutral", declined: "emergency",
};
const LABEL = {
  awaiting_ops_approval: "Awaiting Approval", accepted: "Accepted", team_being_built: "Team Being Built",
  staff_awaiting_confirmation: "Staff Awaiting Confirmation", deployment_confirmed: "Deployment Confirmed",
  staff_en_route: "Staff En Route", live: "Live", completed: "Completed", closed: "Closed", declined: "Declined",
};

export default function CompanyDeploymentCentre() {
  const [data, setData] = useState({ deployments: [], deployment_centre_unlocked: false });
  useEffect(() => { api.get("/company/managed-deployments").then((r) => setData(r.data || { deployments: [] })).catch(() => {}); }, []);

  const active = data.deployments.filter((d) => ["accepted", "team_being_built", "staff_awaiting_confirmation", "deployment_confirmed", "staff_en_route", "live"].includes(d.status));
  const pending = data.deployments.filter((d) => d.status === "awaiting_ops_approval");
  const done = data.deployments.filter((d) => ["completed", "closed", "declined"].includes(d.status));

  return (
    <div className="space-y-6" data-testid="company-deployment-centre">
      <PageHero
        eyebrow="5% Managed Deployment"
        title="Deployment Centre"
        sub="Operations administered by SecuSource"
        right={<StatusPill kind={data.deployment_centre_unlocked ? "active" : "neutral"}>{data.deployment_centre_unlocked ? "Unlocked" : "Locked"}</StatusPill>}
      />

      {!data.deployment_centre_unlocked && pending.length === 0 && (
        <div className="card-dark p-8 text-center" data-testid="dc-empty-locked">
          <Lock className="w-8 h-8 txt-lo mx-auto mb-2" />
          <div className="font-outfit font-semibold txt-hi">Deployment Centre locked</div>
          <div className="txt-md font-manrope text-sm mt-1">Choose the 5% Managed Deployment tier when posting a job to trigger a Managed Deployment Request.</div>
          <Link to="/company/jobs" className="btn-brand mt-4 inline-flex">Post a Job</Link>
        </div>
      )}

      <div className="grid sm:grid-cols-3 gap-4">
        <StatHero label="Active" value={active.length} icon={Activity} tone="emerald" testId="dc-stat-active" />
        <StatHero label="Awaiting Approval" value={pending.length} icon={ShieldCheck} tone="amber" testId="dc-stat-pending" />
        <StatHero label="Archived" value={done.length} icon={CalendarDays} tone="brand" testId="dc-stat-done" />
      </div>

      {active.length > 0 && (
        <div className="space-y-3" data-testid="dc-active-list">
          <div className="eyebrow eyebrow-brand">Active Deployments</div>
          {active.map((d) => <Row key={d.id} d={d} />)}
        </div>
      )}

      {pending.length > 0 && (
        <div className="space-y-3" data-testid="dc-pending-list">
          <div className="eyebrow eyebrow-brand">Awaiting Operations Approval</div>
          {pending.map((d) => <Row key={d.id} d={d} readOnly />)}
        </div>
      )}

      {done.length > 0 && (
        <div className="space-y-3">
          <div className="eyebrow eyebrow-brand">Archive</div>
          {done.map((d) => <Row key={d.id} d={d} readOnly />)}
        </div>
      )}
    </div>
  );
}

function Row({ d, readOnly }) {
  const canOpen = !readOnly || d.status === "completed" || d.status === "closed";
  const Inner = (
    <div className="card-dark card-dark-hover p-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <StatusPill kind={KIND[d.status]}>{LABEL[d.status]}</StatusPill>
            <span className="spill spill-tracked"><span className="dot" /> 5% Managed</span>
            <span className="font-outfit font-semibold txt-hi">{d.job_title}</span>
          </div>
          <div className="grid gap-y-1 gap-x-4 mt-2 sm:grid-cols-2 text-xs txt-md font-manrope">
            <div className="flex items-center gap-1.5"><MapPin className="w-3 h-3 text-brand" /> {d.location}</div>
            <div className="flex items-center gap-1.5"><CalendarDays className="w-3 h-3 text-brand" /> {d.date} · {d.start_time || "—"}–{d.end_time || "—"}</div>
            <div className="flex items-center gap-1.5"><Users2 className="w-3 h-3 text-brand" /> {d.workers_needed}× {d.staff_type || "staff"}</div>
            <div className="eyebrow">Admin: <span className="txt-hi ml-1">{d.assigned_admin_name || "—"}</span></div>
          </div>
        </div>
        <ChevronRight className="w-4 h-4 txt-lo" />
      </div>
    </div>
  );
  return canOpen ? (
    <Link to={`/company/deployment-centre/${d.id}`} data-testid={`dc-row-${d.id}`}>{Inner}</Link>
  ) : (
    <div data-testid={`dc-row-${d.id}`} className="opacity-70">{Inner}</div>
  );
}
